import React, { useEffect } from 'react'
import cx from 'classnames'
import { useDevice } from 'device'
import { constants } from 'helpers'
import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { useIntl } from 'intl'
import cookieStorage from 'cookie-storage'
import { track } from 'analytics'

import { WidthContainer } from 'components/layout'
import { Text } from 'components/dataDisplay'

import GetStartedButton from '../GetStartedButton/GetStartedButton'

import bottlesGif from './images/bottles.gif'
import cartGif from './images/cart.gif'
import caseGif from './images/case.gif'
import handshakeGif from './images/handshake.gif'

import messages from './messages'


type HowItWorksSectionWithAnimationsProps = {
  className?: string
  startButtonMessage?: string | Intl.Message
  showStartButton?: boolean
  withSubscriptionAndShippingInfo?: boolean
  isCanada?: boolean
}

const HowItWorksSectionWithAnimations: React.FunctionComponent<HowItWorksSectionWithAnimationsProps> = (props) => {
  const { className, startButtonMessage, showStartButton = true, withSubscriptionAndShippingInfo, isCanada } = props
  const { isMobile } = useDevice()

  useEffect(() => {
    track('Banner display', {
      content: 'No surprise widget',
      placement: 'How it works',
    })
  }, [])

  const isFreeTrial = cookieStorage.getItem(constants.cookieNames.isFreeTrialCoupon)

  return (
    <WidthContainer className={cx(className, 'flex flex-col items-center')}>
      <Text
        className={cx('text-center', isMobile ? 'mb-56' : 'mb-64')}
        style={isMobile ? 'h5' : 'h3'}
        message={messages.title}
        html
      />
      <div className={cx('flex', isMobile ? 'flex-col gap-40' : 'items-baseline gap-16')}>
        <FeatureItem
          imageSrc={bottlesGif}
          title={messages.features.bottles.title}
          subtitle={messages.features.bottles.subtitle}
        />
        <FeatureItem
          imageSrc={cartGif}
          title={messages.features.cart.title}
          subtitle={messages.features.cart.subtitle}
        />
        <FeatureItem
          imageSrc={caseGif}
          title={messages.features.case.title}
          subtitle={messages.features.case.subtitle}
        />
        <FeatureItem
          imageSrc={handshakeGif}
          title={messages.features.handshake.title}
          subtitle={messages.features.handshake.subtitle}
        />
      </div>
      {
        showStartButton && (
          <GetStartedButton
            className="mt-56"
            title={startButtonMessage}
            width={320}
            fullWidthOnMobile
            placement="How it works"
          />
        )
      }
      {
        withSubscriptionAndShippingInfo && (
          <Text
            className="mt-24"
            style={isMobile ? 'h8' : 'h7'}
            message={isCanada || isFreeTrial ? messages.subscriptionAndShippingInfo.cancelAnytime : messages.subscriptionAndShippingInfo.freeShipping}
            color="gold-70"
          />
        )
      }
    </WidthContainer>
  )
}

type FeatureItemProps = {
  className?: string
  imageSrc: string
  title: string | Intl.Message
  subtitle: string | Intl.Message
}

const FeatureItem: React.FunctionComponent<FeatureItemProps> = (props) => {
  const { className, imageSrc, title, subtitle } = props

  const intl = useIntl()
  const isHowItWorksEnabled = useFeatureIsOn(constants.features.aaHowItWorks)

  const { isMobile } = useDevice()

  return (
    <div className={cx(className, 'flex items-center', isMobile ? 'gap-32' : 'min-w-[273rem] flex-col gap-16 text-center')}>
      <img className="min-h-[80rem] w-[80rem] max-w-[80rem] flex-1" src={imageSrc} alt={intl.formatMessage(messages.imageAlt)} />
      <div className="flex-1">
        <Text
          style="h6"
          message={title}
          html
        />
        <Text
          className="mt-8"
          style="p4"
          message={subtitle}
          html
        />
      </div>
    </div>
  )
}


export default React.memo(HowItWorksSectionWithAnimations)
